<script setup lang="ts">
import type { VPdfViewerProps } from '@vue-pdf-viewer/viewer';
import { VPdfViewer, useLicense, ZoomLevel } from '@vue-pdf-viewer/viewer';
import { useJumpToInitialPage } from '../../composables/pdfViewer/jumpToInitialPage';
import { useLocalization } from '../../composables/pdfViewer/localization';
import { defaultToolbarOptions } from '../../utils/pdfViewer/defaultToolbarOptions';
import { ref, computed } from 'vue';

export type Props = {
  src?: string;
  initialPage?: number | string;
  options?: VPdfViewerProps;
  assetTrackingId?: string;
};

const props = defineProps<Props>();

// @ts-ignore-next-line
useLicense(import.meta.env.VITE_PDF_VIEWER_LICENSE_KEY);

const pdfViewerRef = ref<InstanceType<typeof VPdfViewer>>();

const currentPage = computed<number | undefined>(
  () => pdfViewerRef.value?.pageControl.currentPage,
);

const { startPage, jumpToStartPage } = useJumpToInitialPage(
  props.initialPage,
  currentPage,
  (page: number) => {
    pdfViewerRef.value?.pageControl.goToPage(page);
  },
);

const onLoaded = () => {
  jumpToStartPage();
};

const onError = (error: Error) => {
  console.error('PDF loading error:', error);
};

const trackingContentTarget = window.location;

const localization = useLocalization();
</script>

<template>
  <div>
    <VPdfViewer
      v-if="src"
      ref="pdfViewerRef"
      :src="src"
      v-bind="options"
      :initial-page="startPage"
      :toolbar-options="defaultToolbarOptions"
      :initial-scale="ZoomLevel.PageWidth"
      locale="custom"
      :localization="{ custom: localization }"
      :data-track-content="assetTrackingId ? true : undefined"
      :data-content-name="assetTrackingId"
      data-content-piece="media-document"
      :data-content-target="trackingContentTarget"
      @loaded="onLoaded"
      @load-error="onError"
    >
      <template #loaderImage>
        <font-awesome-icon
          :icon="['far', 'spinner']"
          class="text-2xl animate-spin"
        />
      </template>
    </VPdfViewer>
    <div
      v-else
      class="flex items-center justify-center h-full w-full bg-neutral-200 rounded-t-md p-7"
    >
      <font-awesome-icon
        :icon="['far', 'spinner']"
        class="text-2xl animate-spin"
      />
    </div>
  </div>
</template>

<style>
.vpv-container .vpv-toolbar-wrapper {
  font-family: 'Euclid Square', sans-serif;
}
</style>
